import API_BASE from '../../utils/api';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

const AdminTransport = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [showStopsModal, setShowStopsModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    routeName: '',
    routeNumber: '',
    driverName: '',
    driverPhone: '',
    driverLicense: '',
    vehicleNumber: '',
    vehicleType: 'bus',
    capacity: '',
    status: 'active',
    fuelType: '',
    insuranceExpiry: '',
    fitnessExpiry: '',
    stops: []
  });

  const [stopFormData, setStopFormData] = useState({
    stopName: '',
    stopTime: '',
    stopOrder: '',
    address: '',
    landmark: ''
  });

  useEffect(() => {
    fetchRoutes();
    fetchStudents();
  }, []);

  const fetchRoutes = async () => {
    try {
      const response = await axios.get(`${API_BASE}/transport`);
      // Ensure response.data is an array
      setRoutes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching routes:', error);
      setError('Failed to fetch routes');
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_BASE}/students`);
      // Handle both array and paginated responses
      let studentsData = [];
      if (Array.isArray(response.data)) {
        studentsData = response.data;
      } else if (response.data.students) {
        studentsData = response.data.students;
      }
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      if (editingRoute) {
        await axios.put(`${API_BASE}/transport/${editingRoute._id}`, formData);
        setSuccess('Route updated successfully!');
      } else {
        await axios.post(`${API_BASE}/transport`, formData);
        setSuccess('Route added successfully!');
      }
      setShowModal(false);
      setEditingRoute(null);
      resetForm();
      fetchRoutes();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving route:', error);
      setError(error.response?.data?.error || 'Error saving route');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this route?')) {
      try {
        await axios.delete(`${API_BASE}/transport/${id}`);
        setSuccess('Route deleted successfully!');
        fetchRoutes();
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        console.error('Error deleting route:', error);
        setError('Error deleting route');
      }
    }
  };

  const handleEdit = (route) => {
    setEditingRoute(route);
    setFormData({
      routeName: route.routeName || '',
      routeNumber: route.routeNumber || '',
      driverName: route.driverName || '',
      driverPhone: route.driverPhone || '',
      driverLicense: route.driverLicense || '',
      vehicleNumber: route.vehicleNumber || '',
      vehicleType: route.vehicleType || 'bus',
      capacity: route.capacity || '',
      status: route.status || 'active',
      fuelType: route.fuelType || '',
      insuranceExpiry: route.insuranceExpiry ? route.insuranceExpiry.split('T')[0] : '',
      fitnessExpiry: route.fitnessExpiry ? route.fitnessExpiry.split('T')[0] : '',
      stops: route.stops || []
    });
    setShowModal(true);
  };

  const handleAddStop = async () => {
    if (!selectedRoute) return;

    // Validate stop form
    if (!stopFormData.stopName || !stopFormData.stopTime || !stopFormData.stopOrder) {
      setError('Please fill in all required stop fields');
      return;
    }

    try {
      await axios.post(`${API_BASE}/transport/${selectedRoute._id}/stops`, stopFormData);
      setShowStopsModal(false);
      setStopFormData({
        stopName: '',
        stopTime: '',
        stopOrder: '',
        address: '',
        landmark: ''
      });
      setSuccess('Stop added successfully!');
      fetchRoutes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error adding stop:', error);
      setError('Error adding stop');
    }
  };

  const handleDeleteStop = async (routeId, stopId) => {
    try {
      await axios.delete(`${API_BASE}/transport/${routeId}/stops/${stopId}`);
      setSuccess('Stop deleted successfully!');
      fetchRoutes();
      
      // Update selected route if it's the current one
      if (selectedRoute && selectedRoute._id === routeId) {
        const updatedRoute = routes.find(r => r._id === routeId);
        setSelectedRoute(updatedRoute);
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting stop:', error);
      setError('Error deleting stop');
    }
  };

  const handleAssignStudent = async (routeId, studentId) => {
    if (!studentId) return;
    
    try {
      await axios.post(`${API_BASE}/transport/${routeId}/assign-student`, { studentId });
      setSuccess('Student assigned successfully!');
      fetchRoutes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error assigning student:', error);
      setError('Error assigning student');
    }
  };

  const handleRemoveStudent = async (routeId, studentId) => {
    try {
      await axios.delete(`${API_BASE}/transport/${routeId}/remove-student/${studentId}`);
      setSuccess('Student removed successfully!');
      fetchRoutes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error removing student:', error);
      setError('Error removing student');
    }
  };

  const resetForm = () => {
    setFormData({
      routeName: '',
      routeNumber: '',
      driverName: '',
      driverPhone: '',
      driverLicense: '',
      vehicleNumber: '',
      vehicleType: 'bus',
      capacity: '',
      status: 'active',
      fuelType: '',
      insuranceExpiry: '',
      fitnessExpiry: '',
      stops: []
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
  <div className="px-6 py-4 flex justify-between items-center">
    <h1 className="text-2xl font-semibold text-gray-800">Transport Management</h1>
    <ProfileDropdown />
  </div>
</nav>

        <div className="p-6">
          {/* Status Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg border border-red-400">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg border border-green-400">
              {success}
            </div>
          )}

          {/* Actions */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Transport Routes</h2>
              <button
                onClick={() => {
                  resetForm();
                  setEditingRoute(null);
                  setShowModal(true);
                }}
                className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition flex items-center"
              >
                <span className="mr-2">+</span> Add New Route
              </button>
            </div>
          </div>

          {/* Routes Grid */}
          {routes.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <span className="text-6xl mb-4 block">🚌</span>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Routes Found</h3>
              <p className="text-gray-500 mb-4">Click "Add New Route" to create your first transport route.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {routes.map((route) => (
                <div key={route._id} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition">
                  <div className={`h-2 ${
                    route.status === 'active' ? 'bg-green-500' :
                    route.status === 'maintenance' ? 'bg-yellow-500' : 'bg-gray-500'
                  }`}></div>
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold">{route.routeName}</h3>
                        <p className="text-sm text-gray-500">Route #{route.routeNumber}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(route.status)}`}>
                        {route.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Driver</p>
                        <p className="font-medium">{route.driverName}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Phone</p>
                        <p className="font-medium">{route.driverPhone}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Vehicle</p>
                        <p className="font-medium">{route.vehicleNumber}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Capacity</p>
                        <p className="font-medium">{route.capacity} seats</p>
                      </div>
                    </div>

                    {/* Stops Section */}
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Stops ({route.stops?.length || 0})</h4>
                      <div className="max-h-32 overflow-y-auto bg-gray-50 rounded p-2">
                        {route.stops && route.stops.length > 0 ? (
                          route.stops.sort((a, b) => a.stopOrder - b.stopOrder).map((stop, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm py-1 border-b last:border-0">
                              <span>
                                <span className="font-medium">{stop.stopOrder}.</span> {stop.stopName} ({stop.stopTime})
                              </span>
                              <button
                                onClick={() => handleDeleteStop(route._id, stop._id)}
                                className="text-red-500 hover:text-red-700 text-xs"
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-400 text-sm text-center py-2">No stops added</p>
                        )}
                      </div>
                    </div>

                    {/* Assigned Students */}
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Assigned Students ({route.assignedStudents?.length || 0})</h4>
                      <select
                        className="w-full border rounded-md px-3 py-2 mb-2 text-sm"
                        onChange={(e) => handleAssignStudent(route._id, e.target.value)}
                        value=""
                      >
                        <option value="">Assign student to route</option>
                        {students
                          .filter(s => !route.assignedStudents?.some(as => as._id === s._id))
                          .map(student => (
                            <option key={student._id} value={student._id}>
                              {student.name} ({student.rollNumber})
                            </option>
                          ))
                        }
                      </select>
                      <div className="max-h-32 overflow-y-auto bg-gray-50 rounded p-2">
                        {route.assignedStudents && route.assignedStudents.length > 0 ? (
                          route.assignedStudents.map((student) => (
                            <div key={student._id} className="flex justify-between items-center text-sm py-1 border-b last:border-0">
                              <span>{student.name} ({student.rollNumber})</span>
                              <button
                                onClick={() => handleRemoveStudent(route._id, student._id)}
                                className="text-red-500 hover:text-red-700 text-xs"
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-400 text-sm text-center py-2">No students assigned</p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <button
                        onClick={() => {
                          setSelectedRoute(route);
                          setShowStopsModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Manage Stops
                      </button>
                      <button
                        onClick={() => handleEdit(route)}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(route._id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Route Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingRoute ? 'Edit Route' : 'Add New Route'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-600 hover:text-gray-900 text-xl"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Route Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.routeName}
                    onChange={(e) => setFormData({...formData, routeName: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Route Number *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.routeNumber}
                    onChange={(e) => setFormData({...formData, routeNumber: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.driverName}
                    onChange={(e) => setFormData({...formData, driverName: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver Phone *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.driverPhone}
                    onChange={(e) => setFormData({...formData, driverPhone: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver License *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.driverLicense}
                    onChange={(e) => setFormData({...formData, driverLicense: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Number *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.vehicleNumber}
                    onChange={(e) => setFormData({...formData, vehicleNumber: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Type
                  </label>
                  <select
                    value={formData.vehicleType}
                    onChange={(e) => setFormData({...formData, vehicleType: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="bus">Bus</option>
                    <option value="van">Van</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacity *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.capacity}
                    onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fuel Type
                  </label>
                  <input
                    type="text"
                    value={formData.fuelType}
                    onChange={(e) => setFormData({...formData, fuelType: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Insurance Expiry
                  </label>
                  <input
                    type="date"
                    value={formData.insuranceExpiry}
                    onChange={(e) => setFormData({...formData, insuranceExpiry: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fitness Expiry
                  </label>
                  <input
                    type="date"
                    value={formData.fitnessExpiry}
                    onChange={(e) => setFormData({...formData, fitnessExpiry: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  {editingRoute ? 'Update' : 'Save'} Route
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Stops Modal */}
      {showStopsModal && selectedRoute && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Manage Stops - {selectedRoute.routeName}
              </h3>
              <button
                onClick={() => {
                  setShowStopsModal(false);
                  setSelectedRoute(null);
                }}
                className="text-gray-600 hover:text-gray-900 text-xl"
              >
                ✕
              </button>
            </div>

            {/* Existing Stops */}
            <div className="mb-6">
              <h4 className="font-medium mb-2">Current Stops</h4>
              <div className="max-h-48 overflow-y-auto bg-gray-50 rounded p-2">
                {selectedRoute.stops && selectedRoute.stops.length > 0 ? (
                  selectedRoute.stops.sort((a, b) => a.stopOrder - b.stopOrder).map((stop, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                      <div>
                        <span className="font-medium">{stop.stopOrder}.</span> {stop.stopName}
                        <span className="text-sm text-gray-500 ml-2">({stop.stopTime})</span>
                        {stop.landmark && <span className="text-xs text-gray-400 block">{stop.landmark}</span>}
                      </div>
                      <button
                        onClick={() => handleDeleteStop(selectedRoute._id, stop._id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-center py-4">No stops added yet</p>
                )}
              </div>
            </div>

            {/* Add New Stop */}
            <div>
              <h4 className="font-medium mb-2">Add New Stop</h4>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Stop Name *"
                  value={stopFormData.stopName}
                  onChange={(e) => setStopFormData({...stopFormData, stopName: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="time"
                    placeholder="Stop Time *"
                    value={stopFormData.stopTime}
                    onChange={(e) => setStopFormData({...stopFormData, stopTime: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                  <input
                    type="number"
                    placeholder="Stop Order *"
                    value={stopFormData.stopOrder}
                    onChange={(e) => setStopFormData({...stopFormData, stopOrder: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Address"
                  value={stopFormData.address}
                  onChange={(e) => setStopFormData({...stopFormData, address: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Landmark"
                  value={stopFormData.landmark}
                  onChange={(e) => setStopFormData({...stopFormData, landmark: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                />
                <button
                  onClick={handleAddStop}
                  className="w-full bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                >
                  Add Stop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransport;